export type EventHeaderStats = {
  visibleCount: number;
  visibleGrandTotal: number;
  confirmedCount: number;
  confirmedGrandTotal: number;
};

const CONFIRMED_PIPELINE_EXCLUDED_STATUS_VALUES = new Set(['lost', 'cancelled', 'void']);

const CONFIRMED_PIPELINE_FALLBACK_STATUSES = [
  'confirmed',
  'picking',
  'partially_filled',
  'filled',
  'partially_shipped',
  'shipped',
  'on_rent',
  'partially_returned',
  'returning',
  'returned',
  'ready_to_invoice',
  'invoiced',
  'closed',
];

const normalizeStatus = (status: string | null | undefined): string => (
  typeof status === 'string' ? status.trim().toLowerCase() : ''
);

/**
 * Resolve the same Confirmed+ stage bucket used by the Event Library header.
 * The reference-table order remains authoritative so newly inserted workflow
 * stages automatically land on the correct side of Confirmed.
 */
export function resolveConfirmedPipelineStatuses(
  orderedStatuses: Array<{ status: string }>,
): Set<string> {
  const confirmedIndex = orderedStatuses.findIndex(
    (option) => normalizeStatus(option.status) === 'confirmed',
  );
  const sourceStatuses = confirmedIndex >= 0
    ? orderedStatuses.slice(confirmedIndex).map((option) => option.status)
    : CONFIRMED_PIPELINE_FALLBACK_STATUSES;
  return new Set(
    sourceStatuses
      .map(normalizeStatus)
      .filter((status) => status && !CONFIRMED_PIPELINE_EXCLUDED_STATUS_VALUES.has(status)),
  );
}

export function summarizeEventHeaderRows<Row>(
  rows: Row[],
  confirmedPipelineStatuses: ReadonlySet<string>,
  getStatus: (row: Row) => string | null | undefined,
  getCustomerTotal: (row: Row) => number | null | undefined,
): EventHeaderStats {
  let visibleGrandTotal = 0;
  let confirmedGrandTotal = 0;
  let confirmedCount = 0;

  for (const row of rows) {
    const rawAmount = getCustomerTotal(row);
    const amount = typeof rawAmount === 'number' && Number.isFinite(rawAmount) ? rawAmount : 0;
    visibleGrandTotal += amount;
    if (confirmedPipelineStatuses.has(normalizeStatus(getStatus(row)))) {
      confirmedCount += 1;
      confirmedGrandTotal += amount;
    }
  }

  return {
    visibleCount: rows.length,
    visibleGrandTotal,
    confirmedCount,
    confirmedGrandTotal,
  };
}
